import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
    ODataQuery,
    useListContext,
    UseListContextReturn,
} from './useListContext';
import { ISupplierSupplement } from '../../types/entities/ISupplierSupplement';
import {
    SupplementFilterState,
    SupplementListViewType,
    ISupplementListState,
} from '../../types/supplement/supplementsTypes';
import useTimeZone from '../../customHooks/useTimeZone';
import { getSupplementsInitialStateFilters } from '../../utils/supplements/supplementUtils';
import { sortDirections } from '../../types/lists/listTypes';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import {
    IComplementaryDataState,
    ITaxComplementaryDataOption,
} from '../../types/taxes/taxesTypes';
import {
    supplementCalculationTypeIcons,
    supplierTaxCalculationTypesIcons,
    UnknownOptionIcons,
} from '../../utils/taxes/taxesListUtils';
import { ISupplierTaxCalculationType } from '../../types/entities/ISupplierTaxCalculationType';
import { ISupplierTaxCalculationRuleType } from '../../types/entities/ISupplierTaxCalculationRuleType';
import { ISupplementCalculationType } from '../../types/entities/ISupplementCalculationType';
import { useODataMultipleQueries } from '../../customHooks/useODataMultipleQueries';

interface SupplementsContextType
    extends UseListContextReturn<ISupplementListState, SupplementFilterState> {
    visibleView: SupplementListViewType;
    changeVisibleView: (view: SupplementListViewType) => void;
    supplierHotels: IComplementaryDataState;
    supplierTaxCalculationTypes: IComplementaryDataState;
    supplierTaxCalculationRuleTypes: IComplementaryDataState;
    supplementCalculationTypes: IComplementaryDataState;
    currencies: IComplementaryDataState;
}

const ContextSupplements = createContext<SupplementsContextType | undefined>(
    undefined
);

export const ProviderSupplements = ({ children }: { children: ReactNode }) => {
    const timezone = useTimeZone();
    const INITIAL_STATE_FILTERS = getSupplementsInitialStateFilters(timezone);
    const ITEMS_PER_PAGE = 40;
    const {
        items,
        filters,
        sortDir,
        sortedBy,
        handleFilterChange,
        resetFilters,
        sortByField,
        handleSearch,
        handleClearFilters,
        handleScroll,
        isLoading,
        totalResults,
        currentResults,
        page,
    } = useListContext<
        ISupplierSupplement,
        ISupplementListState,
        SupplementFilterState
    >({
        initialFilters: INITIAL_STATE_FILTERS,
        buildQuery: (filtersToOdata): ODataQuery => {
            return {
                entityName: readOnlyEntities.SupplierSupplement,
                query: {
                    filter: filtersToOdata,
                    select: [
                        'Id',
                        'Enabled',
                        'Name',
                        'Amount',
                        'Priority',
                        'Description',
                        'IdSupplierHotel',
                        'SupplierHotel/Name',
                        'IdSupplierHotelRoomType',
                        'SupplierHotelRoomType/Name',
                        'IdSupplierHotelMealPlan',
                        'SupplierHotelMealPlan/Name',
                        'IdSupplierHotelRatePlan',
                        'SupplierHotelRatePlan/Name',
                        'IdSupplierTaxCalculationType',
                        'SupplierTaxCalculationType/Key',
                        'SupplierTaxCalculationType/Name',
                        'SupplierTaxCalculationType/Caption',
                        'SupplierTaxCalculationType/LocalizedCaption',
                        'IdSupplierTaxCalculationRuleType',
                        'SupplierTaxCalculationRuleType/Key',
                        'SupplierTaxCalculationRuleType/Name',
                        'SupplierTaxCalculationRuleType/Caption',
                        'SupplierTaxCalculationRuleType/LocalizedCaption',
                        'IdSupplementCalculationType',
                        'SupplementCalculationType/Key',
                        'SupplementCalculationType/Name',
                        'SupplementCalculationType/Caption',
                        'SupplementCalculationType/LocalizedCaption',
                        'IdCurrency',
                        'Currency/Name',
                        'EffectivePerson',
                        'FromDate',
                        'ToDate',
                        'OnlyAdults',
                        'OnlyChildren',
                    ],
                    expand: [
                        readOnlyEntities.SupplierHotel,
                        readOnlyEntities.SupplierHotelRoomType,
                        readOnlyEntities.SupplierHotelMealPlan,
                        readOnlyEntities.SupplierHotelRatePlan,
                        readOnlyEntities.SupplierTaxCalculationType,
                        readOnlyEntities.SupplierTaxCalculationRuleType,
                        readOnlyEntities.SupplementCalculationType,
                        readOnlyEntities.Currency,
                    ],
                    orderBy: `${sortedBy} ${sortDir}`,
                    top: ITEMS_PER_PAGE,
                },
            };
        },
        adaptItem: (raw: ISupplierSupplement): ISupplementListState => ({
            Id: raw.Id,
            Enabled: raw.Enabled,
            Name: raw.Name,
            Description: raw.Description,
            IdSupplierHotel: raw.IdSupplierHotel,
            SupplierHotel: raw.SupplierHotel.Name,
            IdSupplierHotelRoomType: raw.IdSupplierHotelRoomType,
            SupplierHotelRoomType: raw.SupplierHotelRoomType.Name,
            IdSupplierHotelMealPlan: raw.IdSupplierHotelMealPlan,
            SupplierHotelMealPlan: raw.SupplierHotelMealPlan.Name,
            IdSupplierHotelRatePlan: raw.IdSupplierHotelRatePlan,
            SupplierHotelRatePlan: raw.SupplierHotelRatePlan.Name,
            FromDate: raw.FromDate,
            ToDate: raw.ToDate,
            IdSupplierTaxCalculationType: raw.IdSupplierTaxCalculationType,
            SupplierTaxCalculationType:
                raw.SupplierTaxCalculationType.LocalizedCaption ||
                raw.SupplierTaxCalculationType.Caption ||
                raw.SupplierTaxCalculationType.Name,
            SupplierTaxCalculationTypeKey: raw.SupplierTaxCalculationType.Key,
            IdSupplierTaxCalculationRuleType:
                raw.IdSupplierTaxCalculationRuleType,
            SupplierTaxCalculationRuleType:
                raw.SupplierTaxCalculationRuleType.LocalizedCaption ||
                raw.SupplierTaxCalculationRuleType.Caption ||
                raw.SupplierTaxCalculationRuleType.Name,
            SupplierTaxCalculationRuleTypeKey:
                raw.SupplierTaxCalculationRuleType.Key,
            IdSupplementCalculationType: raw.IdSupplementCalculationType,
            SupplementCalculationType:
                raw.SupplementCalculationType.LocalizedCaption ||
                raw.SupplementCalculationType.Caption ||
                raw.SupplementCalculationType.Name,
        }),
        itemsPerPage: ITEMS_PER_PAGE,
        initialSortBy: 'DateLastUpdated',
        initialSortDir: sortDirections.DESC,
    });

    const [visibleView, setVisibleView] = useState<SupplementListViewType>(
        SupplementListViewType.TABLE
    );
    const changeVisibleView = (view: SupplementListViewType) => {
        setVisibleView(view);
    };

    const independentQueries = [
        {
            key: 'supplierHotels',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotel,
                select: ['Id', 'Name', 'IdCurrency'],
                orderBy: 'Name asc',
            },
        },
        {
            key: 'supplierTaxCalculationTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierTaxCalculationType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                taxCalcTypes: ISupplierTaxCalculationType[]
            ): ITaxComplementaryDataOption[] => {
                return taxCalcTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon:
                        supplierTaxCalculationTypesIcons[
                            data.Key as keyof typeof supplierTaxCalculationTypesIcons
                        ] || UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_TYPE,
                }));
            },
        },
        {
            key: 'supplierTaxCalculationRuleTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierTaxCalculationRuleType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                ruleTypes: ISupplierTaxCalculationRuleType[]
            ): ITaxComplementaryDataOption[] => {
                return ruleTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon: 'MoreHoriz',
                }));
            },
        },
        {
            key: 'supplementCalculationTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplementCalculationType,
                select: ['Id', 'Name', 'Caption', 'LocalizedCaption', 'Key'],
                orderBy: 'LocalizedCaption asc',
                filter: 'Enabled',
            },
            transform: (
                supplementCalcTypes: ISupplementCalculationType[]
            ): ITaxComplementaryDataOption[] => {
                return supplementCalcTypes.map(data => ({
                    Id: data.Id,
                    Name:
                        data.LocalizedCaption ||
                        data.Caption ||
                        data.Name ||
                        '',
                    Key: data.Key,
                    Icon:
                        supplementCalculationTypeIcons[
                            data.Key as keyof typeof supplementCalculationTypeIcons
                        ] || UnknownOptionIcons.SUPPLEMENT_CALCULATION_TYPE,
                }));
            },
        },
        {
            key: 'currencies',
            endpoint: {
                entityName: readOnlyEntities.Currency,
                select: ['Id', 'Name'],
                orderBy: 'Name asc',
                filter: 'Enabled',
            },
        },
    ];

    const {
        supplierHotels,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useODataMultipleQueries({
        queries: independentQueries,
        dependencies: [],
    });

    return (
        <ContextSupplements.Provider
            value={{
                items,
                filters,
                sortDir,
                sortedBy,
                handleFilterChange,
                sortByField,
                handleSearch,
                handleClearFilters,
                handleScroll,
                isLoading,
                totalResults,
                currentResults,
                page,
                visibleView,
                resetFilters,
                changeVisibleView,
                supplierHotels,
                supplierTaxCalculationTypes,
                supplierTaxCalculationRuleTypes,
                supplementCalculationTypes,
                currencies,
            }}
        >
            {children}
        </ContextSupplements.Provider>
    );
};

export const useSupplementsList = () => {
    const context = useContext(ContextSupplements);
    if (context === undefined) {
        throw new Error(
            'useSupplementsList must be used within a ProviderSupplements'
        );
    }
    return context;
};

export const useSupplementsComplementaryData = () => {
    const {
        supplierHotels,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useContext(ContextSupplements) as SupplementsContextType;

    return {
        supplierHotels,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    };
};
